import role18MfrsSchema from '../../../../assets/forms/role-520000-mfrs.form.json';
import role18MpersSchema from '../../../../assets/forms/role-520000-mpers.form.json';
import { DEI, SSMT } from '../../../constants/qnames';
import { pickSchema, MPERS_ACCOUNTING_STANDARD } from './schema-picker.util';
import { isEmptyCell } from '../validation-field-key.util';

/**
 * Shared business rules for Role 18 (Statement of Cash Flows, Indirect Method).
 *
 * Role 18 is the **highest-risk Tier 4 sheet** in Phase 2 Batch 2:
 *
 *   - It is the source sheet for TWO cross-sheet reconciliations that other
 *     sheets read back (symmetric partnerships):
 *       * Role 8 (SOFP) reads Role 18's `ssmt:CashAndBankBalances` / cash
 *         equivalents total for the SFP cash tie-out.
 *       * Role 12 (SOPL) reads Role 18's `:ProfitLossBeforeTax` for the
 *         starting-reconciliation figure.
 *   - It ALSO carries its OWN cross-sheet equality validations pointing at
 *     Role 8 (`statementOfFinancialPosition`) and Role 12
 *     (`statementOfProfitOrLoss`). The equality rules are declared on
 *     role-520000-mfrs.form.json L38-43 (Role 12 dep) and L2007-2013 (Role 8
 *     dep) — see also the MPERS variant.
 *   - It has a bespoke same-sheet reconciliation chain between
 *     `CashAndCashEquivalents:End` (financing-activities side) and
 *     `CashAndCashEquivalents:End_occurrence2` (details-of-cash-flows side)
 *     declared via `equality` blocks on both controls with `skipIfSourceNull`
 *     / `skipIfTargetNull` conditional flags.
 *   - It has a `consecutiveYearCash` validation anchored on the
 *     `CashAndCashEquivalents:Beginning` control that compares the current
 *     year's beginning balance against the previous year's End /
 *     End_occurrence2 balance when the two financial years are consecutive.
 *
 * Both the editor's role18-520000-statement-of-cash-flows-indirect.component.ts
 * AND `role18CashFlowsIndirectValidator` import from this file so the schema
 * selection literal, column-visibility logic, cross-sheet target tables, and
 * reconciliation field constants live in exactly one place. If the backend
 * ever renames the accounting-standard enum, only this file changes — the
 * parity-check harness will catch any consumer that drifted.
 *
 * ---- Selection semantics (standard MFRS-default convention) --------------
 *
 * Role 18 now follows the SAME MFRS-default convention as every other variant
 * sheet (role 8 / 12 / 15 / 19 / 20 / 23 / 24): the shared `pickSchema(std,
 * mfrs, mpers)` returns the MPERS schema ONLY on an exact match against the
 * shared `MPERS_ACCOUNTING_STANDARD` plural literal; any other value
 * (including undefined and '') selects MFRS. Previously role 18 inverted this
 * (MPERS as the fallback, MFRS only on the exact MFRS literal — matching the
 * historical `CrossReferenceService.isMFRS()` posture), which made an
 * empty/unknown standard silently load MPERS and build the form against the
 * wrong qname prefix. That inversion has been removed to match role 12 (the
 * cross-sheet partner) and the rest of the variant sheets.
 *
 * ---- Cross-sheet dependency surface --------------------------------------
 *
 * Role 18 reads the following slices for its OWN evaluation:
 *
 *   1. `filingPrerequisites` (Role 0) — drives column visibility:
 *        - `NatureOfFinancialStatements` ("Consolidated" / "Separate") gates
 *          whether the Consolidated columns are visible at all. Note the
 *          component's `checkConsolidatedStatus()` uses a STRICT
 *          `=== 'Consolidated'` check (defaults to false when undefined),
 *          unlike role 8 / role 12 which treat undefined as "show
 *          Consolidated". We MIRROR the strict gate here for parity.
 *        - `DisclosureOfFinancialStatementsPreparationForCurrentSubmission`
 *          ("First time preparation ..." vs "Subsequent preparation ...")
 *          gates whether the PreviousYear columns are visible. Default
 *          (unknown) shows PreviousYear.
 *        - `CompanyCurrentFinancialYearStartDate` /
 *          `CompanyPreviousFinancialYearEndDate` are read by the
 *          `consecutiveYearCash` rule's gate — rule fires only when current
 *          year start === previous year end + 1 day.
 *
 *   2. `statementOfFinancialPosition` (Role 8) — provides the
 *      `ssmt:CashAndBankBalances` total Role 18's cash row is compared
 *      against (schema-declared equality block on L2007-2013 of the MFRS
 *      schema; L1332-1338 of the MPERS schema). Dual-column lookup.
 *
 *   3. `statementOfProfitOrLoss` (Role 12) — provides the
 *      `ifrs-full:ProfitLossBeforeTax` / `ifrs-smes:ProfitLossBeforeTax`
 *      total Role 18's row is compared against (schema-declared equality
 *      block on L38-43 of both schemas). Dual-column lookup. MPERS schema
 *      ALSO declares an equality rule for
 *      `ifrs-smes:ProfitLossFromDiscontinuedOperations`.
 *
 * Per `SheetDependencyService`:
 *   statementOfCashFlowsIndirectMethod -> [statementOfFinancialPosition,
 *                                          statementOfProfitOrLoss]
 *   statementOfFinancialPosition       -> [..., statementOfCashFlowsIndirectMethod]
 *   statementOfProfitOrLoss            -> [statementOfCashFlowsIndirectMethod]
 * i.e. role 18 invalidates roles 8 & 12 (upstream partners); roles 8 & 12
 * also invalidate role 18 (cycles by design — the three sheets publish totals
 * that have to agree).
 */

/**
 * Literal value of
 * filingInfo['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements']
 * that selects the MFRS schema. Mirrors CrossReferenceService.isMFRS().
 */
export const ROLE18_MFRS_ACCOUNTING_STANDARD =
  'Malaysian Financial Reporting Standards';

/**
 * Literal selecting the MPERS schema. Re-exported from the shared
 * `schema-picker.util` so the string lives in exactly one place.
 */
export const ROLE18_MPERS_ACCOUNTING_STANDARD = MPERS_ACCOUNTING_STANDARD;

/** Filename of the MFRS (default) variant schema. */
export const ROLE18_MFRS_SCHEMA_FILE = 'role-520000-mfrs.form.json';

/** Filename of the MPERS variant schema. */
export const ROLE18_MPERS_SCHEMA_FILE = 'role-520000-mpers.form.json';

/**
 * Column-visibility gating for Role 18 now lives in the shared, config-driven
 * resolver: see `dimension-visibility.util.ts` (`resolveDimensionVisibility`)
 * and the central `dimension-visibility-registry.ts`
 * (`SHEET_KEYS.statementOfCashFlowsIndirectMethod` → NATURE / PREP gates).
 * Both the mounted editor (via `CrossReferenceService.getVisibility`) and the
 * pure `role18CashFlowsIndirectValidator` call that one resolver, so the
 * former role-local `ROLE18_NATURE_*` / `ROLE18_PREPARATION_*` constants,
 * `Role18ColumnVisibility`, and `resolveRole18ColumnVisibility` were removed
 * to keep a single source. NOTE: the old role-local resolver used a STRICT
 * `=== 'Consolidated'` gate (unknown/undefined → Consolidated HIDDEN); the
 * shared resolver treats an unknown Nature as "Consolidated shown" — the same
 * convergence applied to roles 8 / 12 / 15 / 19.
 */

/**
 * filingPrerequisites date fields used by the `consecutiveYearCash` gate.
 * The rule only fires when current year start === previous year end + 1 day.
 * Mirrors CashFlowReconciliationService.validateConsecutiveYearCash().
 */
export const ROLE18_CURRENT_YEAR_START_FIELD =
  DEI.CompanyCurrentFinancialYearStartDate;
export const ROLE18_PREVIOUS_YEAR_END_FIELD =
  DEI.CompanyPreviousFinancialYearEndDate;

export type Role18Dimension = 'Consolidated' | 'Separate';
export type Role18Period = 'CurrentYear' | 'PreviousYear';

/** Narrow shape of a role 18 schema control used by the pure validator. */
export interface Role18SchemaEqualityRule {
  sheet: string;
  field: string;
  operation?: 'sum' | string;
  message?: string;
  tolerance?: number;
  taxonomy?: string;
  skipIfSourceNull?: boolean;
  skipIfTargetNull?: boolean;
}

export interface Role18ConsecutiveYearCashComparison {
  beginningConsolidationType: 'Consolidated' | 'Separate';
  compareField: string;
}

export interface Role18SchemaControl {
  qname: string;
  label?: string;
  type?: string;
  required?: boolean;
  validators?: Array<{ name?: string; conditionalOn?: unknown }>;
  ui?: {
    controlType?: string;
    control?: string;
    dualColumn?: boolean;
    readonly?: boolean;
    calculated?: boolean;
    linkTo?: { role: string; qname: string };
  };
  validation?: {
    dataType?: string;
    equality?: Role18SchemaEqualityRule[];
    consecutiveYearCash?: {
      comparisons: Role18ConsecutiveYearCashComparison[];
      sheet: string;
      tolerance?: number;
    };
  };
}

export interface Role18Schema {
  role?: string;
  roleId?: string;
  taxonomy?: string;
  sheetId?: string;
  label?: string;
  controls: Role18SchemaControl[];
}

/**
 * Resolve the correct role 18 schema filename for the given accounting
 * standard. Mirrors the component's `loadSchema()` ternary so both paths
 * derive from the same rule.
 */
export function pickRole18SchemaFile(
  accountingStandard: string | undefined | null
): string {
  // Standard MFRS-default convention: MPERS only on the exact shared MPERS
  // literal, MFRS for everything else (incl. undefined/'').
  return pickSchema(
    accountingStandard,
    ROLE18_MFRS_SCHEMA_FILE,
    ROLE18_MPERS_SCHEMA_FILE
  );
}

/**
 * Resolve the actual role 18 schema object for the given accounting
 * standard. Used by the pure validator which cannot fetch() at validate-time
 * (the listing/dashboard's cache-miss path runs synchronously) so both
 * variants are imported eagerly and selected from memory.
 */
export function pickRole18Schema(
  accountingStandard: string | undefined | null
): Role18Schema {
  // Standard MFRS-default convention (see "Selection semantics" above): MFRS
  // is the first value (the default for unknown/empty input) and MPERS the
  // second (chosen only on an exact match against the shared MPERS literal).
  return pickSchema(
    accountingStandard,
    role18MfrsSchema as unknown as Role18Schema,
    role18MpersSchema as unknown as Role18Schema
  );
}

/**
 * Returns true when the accounting standard resolves to MFRS. Mirrors the
 * standard MFRS-default convention used by every other variant sheet: MFRS for
 * anything that is NOT the exact shared MPERS literal (incl. undefined/'').
 */
export function isRole18MFRS(
  accountingStandard: string | undefined | null
): boolean {
  return accountingStandard !== ROLE18_MPERS_ACCOUNTING_STANDARD;
}

/** Map the accounting-standard literal to the equivalent taxonomy tag. */
export function role18AccountingStandardToTaxonomy(
  accountingStandard: string | undefined | null
): 'MFRS' | 'MPERS' {
  return isRole18MFRS(accountingStandard) ? 'MFRS' : 'MPERS';
}

/** Extract MFRS|MPERS from the schema's `taxonomy` field (e.g. "FS-MFRS"). */
export function extractRole18SchemaTaxonomy(
  schema: Role18Schema
): 'MFRS' | 'MPERS' | null {
  const taxonomy = schema.taxonomy;
  if (!taxonomy) return null;
  if (taxonomy.includes('MFRS')) return 'MFRS';
  if (taxonomy.includes('MPERS')) return 'MPERS';
  return null;
}

/**
 * Section-header detection — mirrors the component's `isSectionHeader()`
 * so the validator skips the same controls the editor skips when building
 * the form. Section headers never become FormControls and therefore cannot
 * fail validation.
 */
export function isRole18SectionHeader(control: Role18SchemaControl): boolean {
  return (
    control.type === 'SectionHeader' ||
    control.type === 'TextBlock' ||
    control.ui?.control === 'header' ||
    control.ui?.controlType === 'section-header'
  );
}

/** Monetary-item detection. */
export function isRole18MonetaryItem(control: Role18SchemaControl): boolean {
  return (
    control.type === 'MonetaryItemType' ||
    control.ui?.control === 'number' ||
    control.ui?.controlType === 'number'
  );
}

/** Calculated/readonly check — mirrors `isCalculatedField()`. */
export function isRole18CalculatedControl(
  control: Role18SchemaControl
): boolean {
  return control.ui?.calculated === true || control.ui?.readonly === true;
}

/**
 * Build the bracket-notation form key the component uses for a row+column
 * cell: `qname[Consolidated|Separate][CurrentYear|PreviousYear]`. Mirrors
 * `getXbrlControlName()`.
 */
export function buildRole18ColumnKey(
  qname: string,
  dimension: Role18Dimension,
  period: Role18Period
): string {
  return `${qname}[${dimension}][${period}]`;
}

/**
 * Check if a form-shaped value counts as "empty" for required-field
 * validation. Matches the component's `isNullOrEmpty` guard in
 * `validateRequiredFields()` — null, undefined, and '' all count as empty.
 * Delegates to the shared `isEmptyCell` so every sheet shares one definition.
 */
export function isRole18Empty(value: unknown): boolean {
  return isEmptyCell(value);
}

/**
 * Phase-aware reader: returns the populated filing-info slice from the
 * cross-sheet bundle, preferring `filingInfo` (Phase 2 active source) and
 * falling back to `filingPrerequisites` (Phase 1 active source).
 *
 * Why: under PR-D strict phase-aware hydration, only one of the two slots
 * is written for a given conversion. Validators that hard-code
 * `bundle.get('filingPrerequisites')` see `{}` in Phase 2 conversions and
 * silently degrade (Consolidated hidden, PreviousYear shown by default —
 * the wrong defaults for a Phase 2 filing). Mirrors
 * `FilingFormStateService.getActiveFilingInfoData()` so dashboard / parity
 * paths agree regardless of which slot the caller chose to populate.
 *
 * Returns `undefined` only when both slots are absent or empty.
 */
// Structural shape — accepts the orchestrator's
// `ReadonlyMap<keyof FilingFormState, Record<string, any>>` without dragging
// the FilingFormState type into this rules helper (the rules file is
// intentionally framework-free so it can be imported from anywhere).
interface Role18FilingInfoBundleLike {
  get(
    key: 'filingInfo' | 'filingPrerequisites'
  ): Record<string, unknown> | undefined;
}

export function readRole18ActiveFilingInfo(
  bundle: Role18FilingInfoBundleLike | undefined
): Record<string, unknown> | undefined {
  if (!bundle) return undefined;
  const candidates = [
    bundle.get('filingInfo'),
    bundle.get('filingPrerequisites'),
  ];
  for (const slice of candidates) {
    if (slice && Object.keys(slice).length > 0) {
      return slice;
    }
  }
  return undefined;
}

/**
 * Determine whether the financial year boundary is consecutive enough to
 * trigger the `consecutiveYearCash` rule. Mirrors
 * CashFlowReconciliationService.validateConsecutiveYearCash()'s consecutive-date
 * check exactly: current year start === previous year end + 1 day.
 *
 * Returns false when either date is missing or unparseable — matching the
 * service's silent-skip posture.
 */
export function areRole18YearsConsecutive(
  filingPrerequisites: Record<string, unknown> | undefined
): boolean {
  const startStr = filingPrerequisites?.[ROLE18_CURRENT_YEAR_START_FIELD] as
    | string
    | undefined;
  const endStr = filingPrerequisites?.[ROLE18_PREVIOUS_YEAR_END_FIELD] as
    | string
    | undefined;
  if (!startStr || !endStr) return false;

  const startDate = new Date(startStr);
  const endDate = new Date(endStr);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return false;
  }

  const dayAfterEnd = new Date(endDate);
  dayAfterEnd.setDate(dayAfterEnd.getDate() + 1);
  return startDate.getTime() === dayAfterEnd.getTime();
}

/**
 * Cross-sheet read targets that other sheets (Role 8 SOFP equality; Role 12
 * SOPL equality) pull FROM Role 18. Kept here so cross-sheet partners have
 * a single canonical list to reference — today these are pulled directly
 * from role 18's schema by the SchemaCache path, but documenting the fields
 * here prevents drift as schemas evolve.
 */
export interface Role18CrossSheetReadTargets {
  /** Field that Role 8 compares against for the cash tie-out. */
  cashAndBankBalancesQname: string;
  /** Field that Role 12 compares against for the P&L starting figure. */
  profitLossBeforeTaxQname: string;
}

export function getRole18CrossSheetReadTargets(
  accountingStandard: string | undefined | null
): Role18CrossSheetReadTargets {
  const mfrs = isRole18MFRS(accountingStandard);
  return {
    // Both MFRS and MPERS schemas use the plain 'ssmt:' prefix for
    // CashAndBankBalances — confirmed in schemas L1995 (MFRS) and L1320 (MPERS).
    cashAndBankBalancesQname: SSMT.CashAndBankBalances,
    profitLossBeforeTaxQname: mfrs
      ? 'ifrs-full:ProfitLossBeforeTax'
      : 'ifrs-smes:ProfitLossBeforeTax',
  };
}

/**
 * Reconciliation chain between the two "Cash and cash equivalents at end
 * of period" rows on Role 18 itself:
 *   • `CashAndCashEquivalents:End`              — financing-activities side,
 *     computed from Beginning + NetIncreaseDecrease.
 *   • `CashAndCashEquivalents:End_occurrence2`  — details-of-cash-flows side,
 *     computed from CashAndBankBalances - BankOverdrafts + OtherAdjustments.
 * Both rows declare reciprocal `equality` rules (with `skipIfSourceNull` /
 * `skipIfTargetNull`) so they agree.
 */
export interface Role18EndReconciliationPair {
  endQname: string;
  endOccurrence2Qname: string;
}

export function getRole18EndReconciliationPair(
  accountingStandard: string | undefined | null
): Role18EndReconciliationPair {
  const mfrs = isRole18MFRS(accountingStandard);
  return {
    endQname: mfrs
      ? 'ifrs-full:CashAndCashEquivalents:End'
      : 'ifrs-smes:CashAndCashEquivalents:End',
    endOccurrence2Qname: mfrs
      ? 'ifrs-full:CashAndCashEquivalents:End_occurrence2'
      : 'ifrs-smes:CashAndCashEquivalents:End_occurrence2',
  };
}
